/**
 * useLiveClock - Live-updating clock hook
 * 
 * Features:
 * - Updates every second (or every minute if seconds disabled)
 * - Locale-aware formatting
 * - Returns timeString and dateString
 * - Automatic cleanup on unmount
 */
import { useState, useEffect } from 'react';

export function useLiveClock({ withSeconds = true, locale = 'de-DE' } = {}) {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    // Update interval: 1s if showing seconds, 1min otherwise
    const interval = setInterval(() => {
      setTime(new Date());
    }, withSeconds ? 1000 : 60000);

    return () => clearInterval(interval);
  }, [withSeconds]);

  // Format time string (HH:MM or HH:MM:SS)
  const timeString = time.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {})
  });

  // Format date string (e.g., "Montag, 23. Dezember 2024")
  const dateString = time.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return { timeString, dateString, currentTime: time };
}
